import { makeStyles } from '@material-ui/core/styles'
import { isEmpty } from 'lodash'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import Actions from '../../../actions'
import {
  GRIN_SCOPE_MAX_IN_ORDER,
  GRIN_SCOPE_MIN_IN_ORDER,
  GRIN_SCOPE_TYPE_LG,
  GRIN_SCOPE_TYPE_LG_MINI,
  GRIN_SCOPE_TYPE_MINI,
  GRIN_SCOPE_TYPE_REGULAR
} from '../../../consts/billingConsts'
import { assemblePlanKey } from '../../../utils/billingUtils'
import { resolveScopeMaxQuantity } from '../../../utils/scopeOrderUtils'
import { isMobile } from '../../../utils/mobileUtils'
import BaseModal from '../../common/modals/BaseModal'
import OrderGrinKitsAmountContainer from './OrderGrinKitsAmountContainer'
import OrderGrinKitsComparison from './OrderGrinKitsComparison'
import useFeatureFlags from 'hooks/useFeatureFlags'
import { getScopeProductsContent } from './scopeProducts'
import useScopeOrderValidation from './useScopeOrderValidation'
import TrademarkText from 'components/common/TrademarkText'

const useStyles = makeStyles(() => ({
  body: {
    minWidth: isMobile() ? 0 : 680
  },
  comparisonBody: {
    minWidth: ({ scopeLGOrdering }) => (isMobile() ? 0 : scopeLGOrdering ? 1040 : 680),
    overflowX: 'hidden'
  }
}))

const SelectPlanScopesModal = ({ isOpen, handleClose }) => {
  const { scopeLGOrdering } = useFeatureFlags()
  const classes = useStyles({ scopeLGOrdering })
  const dispatch = useDispatch()
  const { t } = useTranslation()

  const isLoading = useSelector(state => state.billingReducer.order.isLoading)
  const grinPlans = useSelector(state => state.billingReducer.grinPlans)
  const productScope = useSelector(state => state.productReducer.scope.data)
  const productScopeMini = useSelector(state => state.productReducer.scopeMini.data)
  const productScopeLG = useSelector(state => state.productReducer.scopeLG.data)
  const productScopeLGMini = useSelector(state => state.productReducer.scopeLGMini.data)
  const maxQuantityLG = useSelector(state => state.productReducer.maxQuantityLG)
  const maxQuantityLGMini = useSelector(state => state.productReducer.maxQuantityLGMini)
  const productScopePlanGroup = useSelector(state => state.productReducer.scopePlanGroup)
  const loadedIncludeScopeLG = useSelector(state => state.productReducer.includesScopeLG)
  const isLoadingScopeProduct = useSelector(state => state.productReducer.isLoadingScopeProducts)
  const shippingFee = useSelector(state => state.productReducer.shippingFee)
  const selectedPlanGroup = useSelector(state => state.billingReducer.upgradePlan.selectedPlanGroup)
  const selectedPlanPeriod = useSelector(state => state.billingReducer.upgradePlan.selectedPlanPeriod)
  const includeScopeLG = !!scopeLGOrdering
  // LG rows render only when the backend actually returned the product (see OrderGrinKitsModal).
  const hasLoadedScopeProduct = !isEmpty(productScope)
  const canOrderScopeLG = includeScopeLG && !isEmpty(productScopeLG)
  const canOrderScopeLGMini = includeScopeLG && !isEmpty(productScopeLGMini)

  const selectedPlan = grinPlans[assemblePlanKey(selectedPlanGroup, selectedPlanPeriod)]
  const scopeProductsContent = useMemo(() => getScopeProductsContent(t), [t])

  const [quantity, setQuantity] = useState(GRIN_SCOPE_MIN_IN_ORDER)
  const [quantityMini, setQuantityMini] = useState(0)
  const [selectedQuantityLG, setQuantityLG] = useState(0)
  const [selectedQuantityLGMini, setQuantityLGMini] = useState(0)
  const quantityLG = canOrderScopeLG ? selectedQuantityLG : 0
  const quantityLGMini = canOrderScopeLGMini ? selectedQuantityLGMini : 0
  const [showProductComparison, setShowProductComparison] = useState(false)

  const grinKitLGMaxScopeQuantity = useMemo(
    () =>
      resolveScopeMaxQuantity({ backendMaxQuantity: maxQuantityLG, planMaxScopes: selectedPlan?.maxScopes }) ??
      GRIN_SCOPE_MAX_IN_ORDER,
    [maxQuantityLG, selectedPlan?.maxScopes]
  )
  const grinKitLGMiniMaxScopeQuantity = useMemo(
    () =>
      resolveScopeMaxQuantity({ backendMaxQuantity: maxQuantityLGMini, planMaxScopes: selectedPlan?.maxScopes }) ??
      GRIN_SCOPE_MAX_IN_ORDER,
    [maxQuantityLGMini, selectedPlan?.maxScopes]
  )

  const scopeInputs = [
    {
      key: 'quantity',
      type: GRIN_SCOPE_TYPE_REGULAR,
      quantity,
      maxQuantity: selectedPlan?.maxScopes
    },
    {
      key: 'quantityMini',
      type: GRIN_SCOPE_TYPE_MINI,
      quantity: quantityMini,
      maxQuantity: selectedPlan?.maxScopes
    },
    ...(canOrderScopeLG
      ? [
          {
            key: 'quantityLG',
            type: GRIN_SCOPE_TYPE_LG,
            quantity: quantityLG,
            maxQuantity: grinKitLGMaxScopeQuantity
          }
        ]
      : []),
    ...(canOrderScopeLGMini
      ? [
          {
            key: 'quantityLGMini',
            type: GRIN_SCOPE_TYPE_LG_MINI,
            quantity: quantityLGMini,
            maxQuantity: grinKitLGMiniMaxScopeQuantity
          }
        ]
      : [])
  ]
  const { formErrors, validateForm, onQuantityChange, resetValidation } = useScopeOrderValidation(scopeInputs)

  const products = useMemo(
    () => [
      {
        ...scopeProductsContent[GRIN_SCOPE_TYPE_REGULAR],
        description: scopeProductsContent[GRIN_SCOPE_TYPE_REGULAR].selectorDescription,
        price: productScope?.price || 0,
        quantity,
        maxQuantity: selectedPlan?.maxScopes,
        onSetQuantity: setQuantity,
        error: formErrors?.quantity
      },
      {
        ...scopeProductsContent[GRIN_SCOPE_TYPE_MINI],
        description: scopeProductsContent[GRIN_SCOPE_TYPE_MINI].selectorDescription,
        price: productScopeMini?.price || 0,
        quantity: quantityMini,
        maxQuantity: selectedPlan?.maxScopes,
        onSetQuantity: setQuantityMini,
        error: formErrors?.quantityMini
      },
      ...(canOrderScopeLG
        ? [
            {
              ...scopeProductsContent[GRIN_SCOPE_TYPE_LG],
              description: scopeProductsContent[GRIN_SCOPE_TYPE_LG].selectorDescription,
              price: productScopeLG?.price || 0,
              quantity: quantityLG,
              maxQuantity: grinKitLGMaxScopeQuantity,
              onSetQuantity: setQuantityLG,
              error: formErrors?.quantityLG
            }
          ]
        : []),
      ...(canOrderScopeLGMini
        ? [
            {
              ...scopeProductsContent[GRIN_SCOPE_TYPE_LG_MINI],
              description: scopeProductsContent[GRIN_SCOPE_TYPE_LG_MINI].selectorDescription,
              price: productScopeLGMini?.price || 0,
              quantity: quantityLGMini,
              maxQuantity: grinKitLGMiniMaxScopeQuantity,
              onSetQuantity: setQuantityLGMini,
              error: formErrors?.quantityLGMini
            }
          ]
        : [])
    ],
    [
      formErrors?.quantity,
      formErrors?.quantityLG,
      formErrors?.quantityLGMini,
      formErrors?.quantityMini,
      grinKitLGMaxScopeQuantity,
      grinKitLGMiniMaxScopeQuantity,
      productScope?.price,
      productScopeLG?.price,
      productScopeLGMini?.price,
      productScopeMini?.price,
      quantity,
      quantityLG,
      quantityLGMini,
      quantityMini,
      canOrderScopeLG,
      canOrderScopeLGMini,
      scopeProductsContent,
      selectedPlan?.maxScopes
    ]
  )

  const resetFields = useCallback(() => {
    setQuantity(GRIN_SCOPE_MIN_IN_ORDER)
    setQuantityMini(0)
    setQuantityLG(0)
    setQuantityLGMini(0)
    resetValidation()
  }, [resetValidation])

  const handleCancel = useCallback(() => {
    handleClose()
  }, [handleClose])

  const handleNotNowClick = useCallback(() => {
    handleClose()
    dispatch(Actions.setSelectPlanModalVisibility(false))
    dispatch(Actions.setUpgradePlanDialogVisibility(true))
    dispatch(
      Actions.setSelectedPlanScopesQuantityAndAddress({
        planScopesQuantity: 0,
        planScopesMiniQuantity: 0,
        planScopesLGQuantity: 0,
        planScopesLGMiniQuantity: 0
      })
    )
  }, [dispatch, handleClose])

  const handleSubmit = useCallback(() => {
    if (!validateForm()) {
      return
    }

    handleClose()
    dispatch(
      Actions.setSelectedPlanScopesQuantityAndAddress({
        planScopesQuantity: quantity,
        planScopesMiniQuantity: quantityMini,
        planScopesLGQuantity: quantityLG,
        planScopesLGMiniQuantity: quantityLGMini
      })
    )
    dispatch(Actions.setSelectPlanModalVisibility(false))
    dispatch(Actions.setUpgradePlanDialogVisibility(true))
  }, [dispatch, handleClose, quantity, quantityLG, quantityLGMini, quantityMini, validateForm])

  useEffect(() => {
    if (hasLoadedScopeProduct && !canOrderScopeLG && selectedQuantityLG !== 0) {
      setQuantityLG(0)
    }
  }, [hasLoadedScopeProduct, canOrderScopeLG, selectedQuantityLG])

  useEffect(() => {
    if (hasLoadedScopeProduct && !canOrderScopeLGMini && selectedQuantityLGMini !== 0) {
      setQuantityLGMini(0)
    }
  }, [hasLoadedScopeProduct, canOrderScopeLGMini, selectedQuantityLGMini])

  useEffect(() => {
    if (!isOpen) {
      resetFields()
    }
  }, [isOpen, resetFields])

  useEffect(() => {
    if (
      isOpen &&
      !isLoadingScopeProduct &&
      selectedPlanGroup &&
      (productScopePlanGroup !== selectedPlanGroup || loadedIncludeScopeLG !== includeScopeLG)
    ) {
      dispatch(Actions.fetchGrinScopeProducts({ includeScopeLG, grinPlanGroup: selectedPlanGroup }))
    }
  }, [
    dispatch,
    includeScopeLG,
    isLoadingScopeProduct,
    isOpen,
    loadedIncludeScopeLG,
    productScopePlanGroup,
    selectedPlanGroup
  ])

  return isOpen ? (
    showProductComparison ? (
      <BaseModal
        open={showProductComparison}
        primaryLabel={t('general.back')}
        className={classes.comparisonBody}
        withCloseIcon={false}
        onPrimaryBtnClick={() => setShowProductComparison(false)}
      >
        <OrderGrinKitsComparison />
      </BaseModal>
    ) : (
      <BaseModal
        open={isOpen}
        title={<TrademarkText text={t('dialogs.orderScopes.title')} />}
        secondaryLabel={t('dialogs.orderScopes.notNow')}
        primaryLabel={t('general.next')}
        className={classes.body}
        isLoading={isLoading}
        handleClose={handleCancel}
        onSecondaryBtnClick={handleNotNowClick}
        onPrimaryBtnClick={handleSubmit}
        withCloseIcon={false}
        isPrimaryDisabled={!isEmpty(formErrors)}
      >
        <OrderGrinKitsAmountContainer
          products={products}
          shippingFee={shippingFee || 0}
          formErrors={formErrors}
          onQuantityChange={onQuantityChange}
          onShowProductComparison={() => setShowProductComparison(true)}
        />
      </BaseModal>
    )
  ) : null
}

export default SelectPlanScopesModal
